import { t as tr } from './i18n';
import { baseCountry, baseCurrency, baseSign, intlLocale, usesLei } from './i18n';
import { vatRatesOf } from './markets';
import { fmtNum } from './format';

const cents = {
  format: (n: number) =>
    n.toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
};
import type { Currency, Offer, OfferPackage, RfqItem } from './types';

/**
 * Ofertele pe set („pachete”) și verificările lor. Totul e în moneda ofertei;
 * trecerea în lei se face o singură dată, la salvare, cu cursul ofertei.
 *
 * Regula de bază (jurnalul, 28 septembrie): **nu se compară regimuri diferite**.
 * O chirie pe lună pusă lângă un preț de vânzare ar declara câștigător pe cine
 * nu trebuie — o comparație greșită e mai rea decât niciuna.
 */

/** O lună de chirie, cum o socotesc furnizorii de cofraje. */
export const MONTH_DAYS = 30;

/** 'EUR' = euro (doar la firmele în lei); altfel moneda firmei (vezi `baseCurrency`). */
export const currencySign = (c: Currency | undefined): string =>
  c === 'EUR' && usesLei() ? '€' : baseSign();

/**
 * Suma cu moneda ei: „4.045,99 €”, „19,60 €”, „1.234 lei” (fără bani când nu are);
 * la o firmă care nu lucrează în lei, în moneda ei: „£1,234.50”.
 */
export function money(n: number, c: Currency | undefined): string {
  const r = Math.round(n * 100) / 100;
  if (!usesLei()) {
    try {
      return new Intl.NumberFormat(intlLocale(), {
        style: 'currency',
        currency: baseCurrency(),
        minimumFractionDigits: Number.isInteger(r) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(r);
    } catch {
      /* moneda necunoscută motorului: ca mai jos */
    }
  }
  return `${Number.isInteger(r) ? fmtNum(r) : cents.format(r)} ${currencySign(c)}`;
}

/** Zilele socotite: durata cerută, dar nu sub chiria minimă. */
export function billedDays(pkg: OfferPackage, rentDays: number | null | undefined): number {
  const wanted = rentDays ?? pkg.periodDays ?? 0;
  return Math.max(wanted, pkg.minDays ?? 0);
}

/**
 * Cât costă poziția după oferta pe set, în moneda ofertei:
 * - chirie: tariful pe zi × zilele socotite + costurile o singură dată;
 * - vânzare: totalul + costurile o singură dată.
 */
export function packageCost(pkg: OfferPackage, rentDays: number | null | undefined): number {
  if (pkg.periodDays) {
    return (pkg.total / pkg.periodDays) * billedDays(pkg, rentDays) + pkg.oneTime;
  }
  return pkg.total + pkg.oneTime;
}

/** Chiria pe unitate pe lună (de exemplu €/mp/lună), dacă se știe cât acoperă setul. */
export function perUnitMonth(pkg: OfferPackage): number | null {
  if (!pkg.periodDays || !pkg.coveredQty) return null;
  return ((pkg.total / pkg.periodDays) * MONTH_DAYS) / pkg.coveredQty;
}

/**
 * Cât a ofertat furnizorul, când diferă de cerere și nu e destul de puțin încât
 * să fie avertizare (aia o dă `packageChecks`): „pentru 501 mp”. La cofraje nu
 * iese fix suprafața cerută, iar prețul e pe ce s-a ofertat, nu pe ce s-a cerut.
 */
export function coveredText(pkg: OfferPackage, item: RfqItem): string | null {
  const q = pkg.coveredQty;
  if (q == null || q === item.qty || q < item.qty * 0.98) return null;
  return tr('pentru {0} {1}', fmtNum(q), item.unit);
}

/**
 * De ce o ofertă nu se poate pune lângă celelalte pe poziția asta, sau null.
 * O poziție cerută la chirie are nevoie de o ofertă de chirie pe set (altfel
 * nu știm pe ce perioadă e prețul); una cerută la cumpărare, de un preț de vânzare.
 */
export function regimeMismatch(item: RfqItem, offer: Offer): string | null {
  const pkg = offer.packages?.[item.id];
  const rent = item.rentDays != null;
  if (rent && !pkg) return tr('S-a cerut chirie, iar oferta are un preț fără perioadă');
  if (rent && pkg && !pkg.periodDays) return tr('S-a cerut chirie, oferta e de vânzare');
  if (!rent && pkg?.periodDays) return tr('S-a cerut cumpărare, oferta e de chirie');
  return null;
}

/** Toleranța la adunare: rotunjirea fiecărei linii la bani poate lăsa câțiva bani. */
const tolerance = (lines: number) => Math.max(0.05, 0.006 * lines);

/**
 * Ce merită verificat la o ofertă pe set, în cuvinte. Verificări deterministe,
 * făcute de cod, nu de model: modelul transcrie, nu verifică (oferta CPC avea o
 * greșeală de 14.083 € pe care citirea a repetat-o fără să o vadă).
 */
export function packageChecks(pkg: OfferPackage, item: RfqItem, currency: Currency | undefined): string[] {
  const out: string[] = [];

  if (pkg.lines.length) {
    const sum = pkg.lines.reduce((a, l) => a + l.amount, 0);
    if (Math.abs(sum - pkg.total) > tolerance(pkg.lines.length)) {
      out.push(
        tr('Liniile din ofertă adunate dau {0}, nu {1}', money(sum, currency), money(pkg.total, currency)),
      );
    }
    const seen = new Map<string, number>();
    for (const l of pkg.lines) {
      const key = `${l.code}|${l.desc.trim().toLowerCase()}|${l.qty ?? ''}`;
      seen.set(key, (seen.get(key) ?? 0) + 1);
    }
    const twice = [...seen.entries()].filter(([, n]) => n > 1);
    for (const [key, n] of twice) {
      const [code, desc] = key.split('|');
      const line = pkg.lines.find((l) => l.code === code && l.desc.trim().toLowerCase() === desc)!;
      out.push(tr('Linie repetată de {0} ori: {1}', n, [line.code, line.desc].filter(Boolean).join(' ')));
    }
  }

  if (pkg.coveredQty != null && pkg.coveredQty < item.qty * 0.98) {
    out.push(tr('Setul acoperă {0} din {1} {2} cerute', fmtNum(pkg.coveredQty), fmtNum(item.qty), item.unit));
  }
  if (pkg.periodDays && pkg.minDays && item.rentDays != null && item.rentDays < pkg.minDays) {
    out.push(
      tr('Chirie minimă {0} de zile: s-au socotit {1}, nu {2}', pkg.minDays, pkg.minDays, item.rentDays),
    );
  }
  if (pkg.excludes.trim()) out.push(tr('Nu include: {0}', pkg.excludes.trim()));
  return out;
}

/**
 * Verifică totalurile din documentul ofertei: bază + TVA = total cu TVA, iar
 * TVA-ul e una din cotele legale aplicată bazei. Sumele lipsă nu se verifică.
 */
export function vatChecks(
  doc: { net: number | null; vat: number | null; gross: number | null },
  currency: Currency | undefined,
): string[] {
  const out: string[] = [];
  const { net, vat, gross } = doc;
  if (net != null && vat != null && gross != null && Math.abs(net + vat - gross) > 0.05) {
    out.push(
      tr(
        'În ofertă, {0} + TVA {1} nu dau totalul de {2}',
        money(net, currency),
        money(vat, currency),
        money(gross, currency),
      ),
    );
  }
  if (net != null && vat != null && net > 0) {
    // cotele țării firmei (România: 21%, 11% și cele de dinainte)
    const VAT_RATES = vatRatesOf(baseCountry()).map((r) => r / 100);
    const ok = VAT_RATES.some((r) => Math.abs(net * r - vat) <= Math.max(0.05, net * r * 0.0005));
    if (!ok) {
      const exp = net * VAT_RATES[0]!;
      out.push(
        tr(
          'TVA-ul din ofertă ({0}) nu e o cotă legală din bază ({1}); la {2}% ar fi {3}',
          money(vat, currency),
          money(net, currency),
          fmtNum(VAT_RATES[0]! * 100),
          money(exp, currency),
        ),
      );
    }
  }
  return out;
}

/** Totalul nu e o sumă sensibilă (gol, zero, negativ) → pachetul nu se poate salva. */
export const packageValid = (pkg: OfferPackage): boolean =>
  Number.isFinite(pkg.total) &&
  pkg.total > 0 &&
  (pkg.periodDays == null || pkg.periodDays > 0) &&
  pkg.oneTime >= 0;

/** Durata, în cuvinte: „30 de zile”, „o zi”, „3 luni”. */
export function daysText(d: number): string {
  if (d === 1) return tr('o zi');
  if (d % MONTH_DAYS === 0 && d >= MONTH_DAYS * 2) return tr('{0} luni', d / MONTH_DAYS);
  if (d === MONTH_DAYS) return tr('o lună');
  return d < 20 ? tr('{0} zile', d) : tr('{0} de zile', d);
}
